import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import classNames from "classnames";
import { TbCash, TbCoins, TbLogin2, TbPlaneTilt, TbUsers } from "react-icons/tb";
import { formatNumber } from "src/utility";
import type { OrderDetailsResponse } from "src/Modules/Flights/constants";
import JourneyCardTags from "src/Modules/Flights/Pages/JourneyListing/components/JourneyCard/components/JourneyCardTags/JourneyCardTags";

type SummaryChipItemProps = { title: ReactNode; subText: ReactNode; subTitle?: ReactNode; icon?: ReactNode; iconClassName?: string };

const SummaryChipItem: FC<SummaryChipItemProps> = ({ title, subText, subTitle, icon, iconClassName }) => {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2 md:py-0">
      {icon && (
        <div
          className={classNames(
            "flex size-10 shrink-0 items-center justify-center rounded-md text-xl",
            iconClassName ?? "bg-background-primary-lighter text-font-active",
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="text-font-subtle text-xs uppercase">{title}</div>
        <div className="text-font-default truncate text-sm font-semibold">{subText}</div>
        {subTitle && <div className="text-font-subtle truncate text-xs">{subTitle}</div>}
      </div>
    </div>
  );
};

const PaymentSplitItem: FC<{ title: ReactNode; value: ReactNode; icon: ReactNode }> = ({ title, value, icon }) => (
  <div className="flex items-center gap-3 px-4 py-2 md:py-0">
    <div className="bg-background-primary-lighter text-font-active flex size-10 shrink-0 items-center justify-center rounded-md text-xl">{icon}</div>
    <div className="flex flex-col">
      <div className="text-font-subtle text-xs">{title}</div>
      <div className="text-font-default text-sm font-semibold">{value}</div>
    </div>
  </div>
);

const formatTravelDates = (start: dayjs.Dayjs, end?: dayjs.Dayjs) => {
  if (!end || start.isSame(end, "day")) return start.format("ddd D MMM YYYY");
  const startFormat = start.isSame(end, "year") ? "ddd D MMM" : "ddd D MMM YYYY";
  return `${start.format(startFormat)} - ${end.format("ddd D MMM YYYY")}`;
};

const SummaryChip: FC<{ orderDetails: OrderDetailsResponse }> = ({ orderDetails }) => {
  const { t } = useTranslation();
  const { segments = [], passengers = [], fareSummary } = orderDetails;

  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const isRoundTrip = segments.some((segment) => segment.direction === "Inbound");
  const isMultiCity = !isRoundTrip && segments.length > 1;
  const tripType = isRoundTrip ? "roundTrip" : isMultiCity ? "multiCity" : "oneWay";

  const maxStops = Math.max(0, ...segments.map((segment) => segment.stops ?? 0));
  const routeType = maxStops === 0 ? "direct" : maxStops === 1 ? "oneStop" : "multiStop";

  const travelDates = firstSegment
    ? formatTravelDates(dayjs(firstSegment.departure), segments.length > 1 ? dayjs(lastSegment.departure) : undefined)
    : "-";

  const leadPassenger = passengers.find((passenger) => passenger.isLeadPax) ?? passengers[0];
  const leadName = leadPassenger ? `${leadPassenger.firstName} ${leadPassenger.lastName}` : "-";
  const othersCount = Math.max(0, passengers.length - 1);

  const currency = fareSummary?.currency ?? "";
  const cashPaid = orderDetails.cashAmountPaid ?? orderDetails.totalAmount ?? 0;
  const pointsRedeemed = orderDetails.totalPointsRedeemed ?? 0;

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col rounded-md border shadow-xs">
      <div className="divide-border-subtle flex w-full flex-col divide-y px-2 py-4 md:flex-row md:divide-x md:divide-y-0">
        <SummaryChipItem
          icon={<TbPlaneTilt />}
          title={t("BookingResult.flightChips.route")}
          subText={`${orderDetails.origin} → ${orderDetails.destination}`}
          subTitle={t(`BookingResult.flightChips.routeType.${routeType}`)}
        />
        <SummaryChipItem
          icon={<TbLogin2 />}
          title={t("BookingResult.flightChips.travelDates")}
          subText={travelDates}
          subTitle={t(`BookingResult.flightChips.tripType.${tripType}`)}
        />
        <SummaryChipItem
          icon={<TbUsers />}
          title={t("BookingResult.flightChips.passengers")}
          subText={othersCount > 0 ? `${leadName} ${t("BookingResult.flightChips.others", { count: othersCount })}` : leadName}
          subTitle={`${t("BookingResult.flightChips.passengersCount", { count: passengers.length })} • ${t("BookingResult.flightChips.primary")}`}
        />
        <SummaryChipItem
          title={t("BookingResult.flightChips.fare")}
          subText={<JourneyCardTags tags={{ isRefundable: !orderDetails.nonRefundable, isLcc: false }} />}
        />
      </div>
      <div className="border-border-subtle flex flex-col gap-3 border-t px-7 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col">
          <div className="text-font-subtle text-xs">{t("BookingResult.paymentReceipt.totalAmount")}</div>
          <div className="text-font-default text-2xl font-semibold">{`${currency} ${formatNumber(orderDetails.totalAmount ?? 0)}`.trim()}</div>
        </div>
        <div className="divide-border-subtle flex flex-col divide-y md:flex-row md:divide-x md:divide-y-0">
          <PaymentSplitItem icon={<TbCash />} title={t("BookingResult.paymentReceipt.cashPaid")} value={`${formatNumber(cashPaid)} ${currency}`.trim()} />
          <PaymentSplitItem
            icon={<TbCoins />}
            title={t("BookingResult.paymentReceipt.pointsRedeemed")}
            value={t("BookingResult.paymentReceipt.pointsValue", { points: formatNumber(pointsRedeemed) })}
          />
        </div>
      </div>
    </div>
  );
};

export default SummaryChip;
